import React from "react";
import { NavLink } from "react-router-dom";
import Chatbot from "../Chatbot";
import { trackCTA } from "../../utils/analytics";

const Home = () => {
  return (
    <div className="home">
      <section className="relative bg-gradient-to-r from-jnanakshi-500 via-jnanakshi-400 to-jnanakshi-600 text-white overflow-hidden">
        {/* Subtle background image overlay */}
        <div
          className="absolute inset-0 bg-center bg-cover opacity-15 mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url('/images/home-hero-overlay.jpg')"
          }}
        />
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 flex flex-col-reverse lg:flex-row items-center gap-6 sm:gap-8 lg:gap-10 py-16 sm:py-20 md:py-24 lg:py-32 min-h-[500px] sm:min-h-[600px] lg:min-h-[700px]">
          {/* Hero Text */}
          <div className="lg:w-1/2 text-center lg:text-left">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-extrabold leading-tight">
              The Eye of Knowledge
            </h1>
            <p className="mt-4 sm:mt-6 text-base sm:text-lg md:text-xl text-gray-200 text-justify lg:text-left">
              Welcome to <strong className="text-xl sm:text-2xl md:text-3xl font-extrabold bg-gradient-to-b from-white to-jnanakshi-200 bg-clip-text text-transparent tracking-wide uppercase">
                Jnanakshi Solutions</strong>. We bridge the gap between academic knowledge and professional competence through immersive, live workshops and targeted training programs that empower college students to become industry‑ready, directly connected with seasoned industry experts.
            </p>
            {/* Delivery Modes & Mentoring Badges */}
            <div className="mt-5 flex flex-wrap gap-2 justify-center lg:justify-start">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs sm:text-sm font-semibold">
                Online & Offline Training
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs sm:text-sm font-semibold">
                Individual Mentoring Available
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/15 backdrop-blur text-xs sm:text-sm font-semibold">
                AIML: Basic (ML) & Advanced (DL/GenAI)
              </span>
            </div>
            <div className="mt-6 sm:mt-8">
              <NavLink
                to="/about-us"
                className="inline-block px-4 sm:px-6 py-2 sm:py-3 bg-white text-jnanakshi-600 font-semibold rounded-lg shadow-lg hover:bg-gray-100 transition text-sm sm:text-base"
                onClick={() => trackCTA('learn_more_home')}
              >
                Learn More
              </NavLink>
            </div>
          </div>
          {/* Hero Image */}
          <div className="lg:w-1/2 flex items-center justify-center lg:justify-end">
            <img
              src="/Picture4.png"
              alt="Jnanakshi Solutions - The Eye of Knowledge"
              className="max-w-full h-auto max-h-48 sm:max-h-64 md:max-h-72 lg:max-h-80 xl:max-h-96 object-contain"
            />
          </div>
        </div>

        {/*WAVES*/}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0] rotate-180">
          <svg
            className="relative block w-[calc(100%+1.3px)] h-[111px]"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
          >
            <path
              d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
              className="fill-white opacity-25"
            ></path>
            <path
              d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
              className="fill-white opacity-50"
            ></path>
            <path
              d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
              className="fill-gray-50"
            ></path>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gray-50 dark:bg-dark-800 py-12 sm:py-16 lg:py-20 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 dark:text-white text-center">
            Why Choose Jnanakshi Solutions?
          </h2>
          <p className="text-center text-gray-600 dark:text-gray-300 mt-3 sm:mt-4 text-sm sm:text-base md:text-lg max-w-4xl mx-auto">
            We provide immersive live workshops and targeted training programs to help college students become industry-ready professionals.
          </p>
          <div className="mt-8 sm:mt-10 lg:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Feature Cards */}
            {[
              {
                title: "Live Workshops",
                description: "Immersive sessions led by seasoned industry experts offering real-world insights.",
                image: "/images/home-feature-workshops.jpg",
              },
              {
                title: "Industry Experts",
                description: "Direct connection with professionals who provide hands-on skills and mentorship.",
                image: "/images/home-feature-experts.jpg",
              },
              {
                title: "Career Ready",
                description: "Transform academic knowledge into professional competence for the job market.",
                image: "/images/home-feature-career.jpg",
              },
            ].map((feature, index) => (
              <div
                key={index}
                className="flex flex-col items-center text-center p-4 sm:p-6 bg-white dark:bg-dark-700 shadow-md rounded-lg transition-colors duration-300 hover:shadow-lg transform hover:-translate-y-1 transition-all duration-300"
              >
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="w-full h-32 sm:h-40 object-cover rounded-md mb-3 sm:mb-4"
                  loading="lazy"
                />
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 dark:text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="bg-white dark:bg-dark-900 py-12 sm:py-16 lg:py-20 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 dark:text-white text-center">Our Programs</h2>
          <p className="text-center text-gray-600 dark:text-gray-300 mt-3 sm:mt-4 text-sm sm:text-base md:text-lg max-w-4xl mx-auto">
            We offer immersive workshops, exclusive training, and personalized mentoring to help students become industry‑ready.
          </p>
          <div className="mt-8 sm:mt-10 lg:mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 items-stretch">
            {/* Workshops */}
            <div className="relative bg-white dark:bg-dark-700 p-4 sm:p-6 lg:p-8 rounded-2xl shadow-md hover:shadow-xl transition duration-300 transform hover:-translate-y-1 ring-1 ring-gray-100 dark:ring-dark-600">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-jnanakshi-500 to-jnanakshi-600 rounded-t-2xl"></div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-jnanakshi-50 dark:bg-jnanakshi-900 text-jnanakshi-600 dark:text-jnanakshi-400 flex items-center justify-center text-xl sm:text-2xl mb-3 sm:mb-4 shadow-sm">🎓</div>
              <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white mb-3">Workshops</h3>
              <ul className="text-gray-600 dark:text-gray-300 space-y-2.5 text-sm sm:text-base">
                <li className="flex flex-col items-start gap-0.5">
                  <div className="flex items-start gap-2">
                    <span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span>
                    <span className="font-semibold text-gray-900 dark:text-white">CyberSecurity</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-550 text-white dark:bg-red-950 dark:text-red-400">Specialized</span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 pl-6 leading-tight">Training in VAPT, SOC, GRC & Corporate Networking</span>
                </li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>AI & ML (AIML)</span></li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>Agentic AI</span></li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>Design Thinking</span></li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>Cloud Computing</span></li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>Soft Skills, Python</span></li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>Java</span></li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>Database - SQL, etc.</span></li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>and many more ...</span></li>
              </ul>
            </div>

            {/* Exclusive Training */}
            <div className="relative bg-white dark:bg-dark-700 p-4 sm:p-6 lg:p-8 rounded-2xl shadow-md hover:shadow-xl transition duration-300 transform hover:-translate-y-1 ring-1 ring-gray-100 dark:ring-dark-600">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-jnanakshi-500 to-jnanakshi-600 rounded-t-2xl"></div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-jnanakshi-50 dark:bg-jnanakshi-900 text-jnanakshi-600 dark:text-jnanakshi-400 flex items-center justify-center text-xl sm:text-2xl mb-3 sm:mb-4 shadow-sm">💻</div>
              <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white mb-3">Exclusive Training</h3>
              <ul className="text-gray-600 dark:text-gray-300 space-y-2.5 text-sm sm:text-base">
                <li className="flex flex-col items-start gap-0.5">
                  <div className="flex items-start gap-2">
                    <span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span>
                    <span className="font-semibold text-gray-900 dark:text-white">CyberSecurity</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-555 text-white dark:bg-red-955 dark:text-red-400">Specialized</span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400 pl-6 leading-tight">Training in VAPT, SOC, GRC & Corporate Networking</span>
                </li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>AI & ML (AIML)</span></li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>Python</span></li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>Java</span></li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>Database - SQL, etc.</span></li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>ERP (SAP)</span></li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>Legal Literacy (LLB/BL)</span></li>
              </ul>
            </div>

            {/* Guidance & Mentoring */}
            <div className="relative bg-white dark:bg-dark-700 p-4 sm:p-6 lg:p-8 rounded-2xl shadow-md hover:shadow-xl transition duration-300 transform hover:-translate-y-1 ring-1 ring-gray-100 dark:ring-dark-600 md:col-span-2 lg:col-span-1">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-jnanakshi-500 to-jnanakshi-600 rounded-t-2xl"></div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-jnanakshi-50 dark:bg-jnanakshi-900 text-jnanakshi-600 dark:text-jnanakshi-400 flex items-center justify-center text-xl sm:text-2xl mb-3 sm:mb-4 shadow-sm">🤝</div>
              <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white mb-3">Guidance & Mentoring</h3>
              <ul className="text-gray-600 dark:text-gray-300 space-y-2 text-sm sm:text-base">
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>Internships</span></li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>Career Mentoring</span></li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>Industry Connections</span></li>
                <li className="flex items-start gap-2"><span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span><span>Stress Management</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Career Guidance Section */}
      <section className="bg-gray-50 dark:bg-dark-800 py-12 sm:py-16 lg:py-20 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 dark:text-white">
              Career Guidance for School Students
            </h2>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-300">
              We help school students from 8th to 12th grade understand all the career paths available to them, beyond just Engineering and Medicine,
              so they can choose streams and programs that match their interests and strengths.
            </p>
          </div>

          <div className="mt-8 sm:mt-10 lg:mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 items-stretch">
            {/* 8th–10th Grade Card */}
            <div className="relative bg-white dark:bg-dark-700 p-5 sm:p-7 rounded-2xl shadow-md hover:shadow-xl transition duration-300 transform hover:-translate-y-1 ring-1 ring-gray-100 dark:ring-dark-600">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-jnanakshi-500 to-jnanakshi-600 rounded-t-2xl"></div>
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-jnanakshi-50 dark:bg-jnanakshi-900 text-jnanakshi-600 dark:text-jnanakshi-400 flex items-center justify-center text-xl sm:text-2xl shadow-sm">
                  🎒
                </div>
                <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white">
                  8th–10th Grade: Stream Awareness
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base mb-4">
                We help students and parents understand the options available after 10th standard — Science, Commerce, Arts & Humanities, and Vocational & Skill‑based streams.
              </p>
              <ul className="text-gray-600 dark:text-gray-300 space-y-2 text-sm sm:text-base mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span>
                  <span>Explain each stream and the type of careers it can lead to.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span>
                  <span>Discuss the student’s interests, strengths, and preferences.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span>
                  <span>Guide parents on how to support their child’s career decisions early.</span>
                </li>
              </ul>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                Detailed options and session plans are shared during the guidance session. Please contact us to know more.
              </p>
            </div>

            {/* After 12th Card */}
            <div className="relative bg-white dark:bg-dark-700 p-5 sm:p-7 rounded-2xl shadow-md hover:shadow-xl transition duration-300 transform hover:-translate-y-1 ring-1 ring-gray-100 dark:ring-dark-600">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-jnanakshi-500 to-jnanakshi-600 rounded-t-2xl"></div>
              <div className="flex items-center gap-3 mb-4 sm:mb-5">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-jnanakshi-50 dark:bg-jnanakshi-900 text-jnanakshi-600 dark:text-jnanakshi-400 flex items-center justify-center text-xl sm:text-2xl shadow-sm">
                  🎯
                </div>
                <h3 className="text-xl sm:text-2xl font-extrabold text-gray-900 dark:text-white">
                  After 12th: UG & PG Pathways
                </h3>
              </div>
              <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base mb-4">
                We explain undergraduate and postgraduate options such as B.Tech, B.Com, BBA, B.Sc, M.Tech, M.Com, MBA and many more,
                helping students understand where each program can lead.
              </p>
              <ul className="text-gray-600 dark:text-gray-300 space-y-2 text-sm sm:text-base mb-4">
                <li className="flex items-start gap-2">
                  <span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span>
                  <span>Discuss career paths aligned with different UG and PG programs.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span>
                  <span>Help students choose courses based on interest, aptitude, and long‑term goals.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-jnanakshi-600 dark:text-jnanakshi-400 mt-1">✓</span>
                  <span>Guide parents and students together to align expectations.</span>
                </li>
              </ul>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                For detailed guidance plans and one‑to‑one sessions, please contact us through the Contact page.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Session Feedback Section */}
      <section className="bg-white dark:bg-dark-900 py-12 sm:py-16 lg:py-20 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-jnanakshi-600 to-jnanakshi-400 bg-clip-text text-transparent">
              Impacting Lives: Our First School Session
            </h2>
            <p className="mt-3 sm:mt-4 text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-300">
              Reflecting on our inaugural Career Guidance session conducted for Grade 8-10 students in rural India.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            {/* Session Story and Testimonial */}
            <div className="lg:col-span-6 flex flex-col gap-6">
              {/* Quote card */}
              <div className="relative bg-gradient-to-br from-jnanakshi-50 via-white to-jnanakshi-100/20 dark:from-dark-700 dark:via-dark-850 dark:to-dark-800 p-6 sm:p-8 rounded-2xl border border-jnanakshi-100/40 dark:border-dark-600 shadow-md flex flex-col justify-between">
                <span className="absolute top-2 left-2 text-6xl text-jnanakshi-200/40 dark:text-dark-600 font-serif leading-none select-none">“</span>
                <div className="relative z-10 flex flex-col justify-between mt-2">
                  <p className="text-base sm:text-lg font-medium text-gray-800 dark:text-gray-100 italic leading-relaxed">
                    &ldquo;Thank you for the session, Sir. It was really nice to meet you.&rdquo;
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-4 leading-relaxed">
                    Those simple words from a student at a rural high school in Nathavaram, Visakhapatnam District, validation that we are on the right path.
                  </p>
                  <div className="mt-6 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-jnanakshi-500 text-white flex items-center justify-center font-bold shadow-sm">
                      S
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 dark:text-white">A Grade 8-10 Student</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400">Nathavaram, Visakhapatnam District</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* MD Response Card */}
              <div className="bg-white dark:bg-dark-700 p-6 rounded-2xl shadow-md border border-gray-100 dark:border-dark-600 space-y-4">
                <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base leading-relaxed text-justify">
                  We recently conducted our very first Career Guidance session for students in this beautiful rural school. The energy, curiosity, and appreciation from the students were overwhelming. When that student walked up to our Managing Director, <strong>Sastry Pulya</strong>, it reminded us exactly why we started Jnanakshi Solutions.
                </p>
                <div className="pt-2">
                  <a
                    href="https://www.linkedin.com/posts/jnanakshi-solutions_jnanakshisolutions-education-ruralindia-activity-7410337063725887489-zUEB"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-md transition-colors text-sm"
                  >
                    <i className="fab fa-linkedin-in text-white text-base"></i>
                    View on LinkedIn
                  </a>
                </div>
              </div>
            </div>

            {/* Photo Gallery & Overview */}
            <div className="lg:col-span-6 flex flex-col">
              <div className="bg-gray-50 dark:bg-dark-800 p-6 sm:p-8 rounded-2xl border border-gray-100 dark:border-dark-700 flex flex-col gap-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3">Empowering Rural Youth</h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base leading-relaxed text-justify">
                    At Jnanakshi Solutions, we believe the youth are the main power of India. Whether in a metro city or a rural district, every student deserves the guidance to unlock their potential. Our session was structured to help students understand all career streams available to them, align expectations between parents & students, and encourage them to explore their strengths early.
                  </p>
                </div>

                <div>
                  {/* Gallery Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {[
                      { id: 1, fallback: "/images/home-feature-career.jpg", alt: "Career guidance presentation" },
                      { id: 2, fallback: "/images/home-feature-workshops.jpg", alt: "Active student interactions" },
                      { id: 3, fallback: "/images/home-feature-experts.jpg", alt: "MD Sastry Pulya & Students in Nathavaram" }
                    ].map((img, idx) => (
                      <div key={idx} className="relative rounded-xl overflow-hidden group shadow-md aspect-[4/3] bg-gray-200 dark:bg-dark-700 border border-gray-200 dark:border-dark-600">
                        <img
                          src={`/images/feedback-${img.id}.jfif`}
                          alt={img.alt}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = img.fallback;
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2.5">
                          <span className="text-white text-xs font-semibold leading-tight">{img.alt}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advanced Training & Development Section */}
      <section className="bg-gray-50 dark:bg-dark-800 py-12 sm:py-16 lg:py-20 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-10 lg:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 dark:text-white mb-3 sm:mb-4">
              Advanced Training & Development
            </h2>
            <p className="text-center text-gray-600 dark:text-gray-300 text-sm sm:text-base md:text-lg max-w-4xl mx-auto">
              From AI-powered Python development to modern no-code solutions, we provide cutting-edge training and development services.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Python with AI Training */}
            <div className="bg-white dark:bg-dark-700 p-6 sm:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 text-white flex items-center justify-center text-2xl sm:text-3xl mb-4 sm:mb-6 shadow-lg">
                🐍
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
                AI with Python Training
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base mb-4 leading-relaxed">
                Master Python for AI development with hands-on projects. Learn machine learning fundamentals starting with Linear Regression.
              </p>

              <div className="bg-gray-50 dark:bg-dark-600 p-3 sm:p-4 rounded-lg mb-4">
                <h4 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white mb-2">Example: Linear Regression</h4>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 font-mono bg-gray-100 dark:bg-dark-700 p-2 rounded overflow-x-auto">
                  <div>import numpy as np</div>
                  <div>from sklearn.linear_model import LinearRegression</div>
                  <div># Predict house prices using area</div>
                  <div>model = LinearRegression()</div>
                  <div>model.fit(X_train, y_train)</div>
                </div>
              </div>

              <ul className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm space-y-1">
                <li className="flex items-center gap-2">
                  <span className="text-blue-500">✓</span>
                  <span>Machine Learning Fundamentals</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-500">✓</span>
                  <span>Data Science with Pandas & NumPy</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-blue-500">✓</span>
                  <span>AI Model Deployment</span>
                </li>
              </ul>
            </div>

            {/* No-Code/Low-Code Development */}
            <div className="bg-white dark:bg-dark-700 p-6 sm:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-green-500 to-teal-600 text-white flex items-center justify-center text-2xl sm:text-3xl mb-4 sm:mb-6 shadow-lg">
                🚀
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
                No-Code/Low-Code Development
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base mb-4 leading-relaxed">
                Build applications faster using no-code platforms like Glide and Bubble. Create powerful apps without extensive coding.
              </p>

              <div className="bg-gray-50 dark:bg-dark-600 p-3 sm:p-4 rounded-lg mb-4">
                <h4 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white mb-2">Glide & Bubble</h4>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                  <div>• Glide: Airtable/Sheets powered apps with responsive UI</div>
                  <div>• Bubble: Full web app builder with workflows</div>
                  <div>• Simple API/database integrations</div>
                  <div>• Rapid prototyping & deployment</div>
                </div>
              </div>

              <ul className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm space-y-1">
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Glide App Development</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Bubble App Development</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>API Integration Training</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-green-500">✓</span>
                  <span>Rapid Prototyping</span>
                </li>
              </ul>
            </div>

            {/* Full Development Services */}
            <div className="bg-white dark:bg-dark-700 p-6 sm:p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 text-white flex items-center justify-center text-2xl sm:text-3xl mb-4 sm:mb-6 shadow-lg">
                💻
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
                Development Services
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm sm:text-base mb-4 leading-relaxed">
                End-to-end development solutions from concept to deployment. We build websites, products, and AI solutions.
              </p>

              <div className="bg-gray-50 dark:bg-dark-600 p-3 sm:p-4 rounded-lg mb-4">
                <h4 className="text-sm sm:text-base font-semibold text-gray-800 dark:text-white mb-2">Our Expertise</h4>
                <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-300">
                  <div>• Modern Web Applications</div>
                  <div>• AI-Powered Solutions</div>
                  <div>• Mobile-First Design</div>
                  <div>• Cloud Deployment</div>
                </div>
              </div>

              <ul className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm space-y-1">
                <li className="flex items-center gap-2">
                  <span className="text-orange-500">✓</span>
                  <span>Website Development</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-orange-500">✓</span>
                  <span>Product Development</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-orange-500">✓</span>
                  <span>AI Development</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer Component */}
      <footer className="bg-gray-800 dark:bg-dark-800 text-white py-8 sm:py-10 lg:py-12 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* About Section */}
            <div className="sm:col-span-2 lg:col-span-1">
              <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">About Jnanakshi Solutions</h3>
              <p className="text-gray-400 dark:text-gray-300 text-sm sm:text-base leading-relaxed">
                At Jnanakshi Solutions, we are committed to bridging the critical gap between academic knowledge
                and professional competence. We specialize in providing immersive live workshops and targeted
                training programs designed to empower college students and transform them into industry-ready professionals.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <NavLink
                    to="/about-us"
                    className="text-gray-400 dark:text-gray-300 hover:text-white transition text-sm sm:text-base"
                  >
                    About Us
                  </NavLink>
                </li>
                <li>
                  <NavLink
                    to="/services"
                    className="text-gray-400 dark:text-gray-300 hover:text-white transition text-sm sm:text-base"
                  >
                    Services
                  </NavLink>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4">Contact Us</h3>
              <p className="text-gray-400 dark:text-gray-300 text-sm sm:text-base">
                <strong>Address:</strong> Bangalore, India
              </p>
              <p className="text-gray-400 dark:text-gray-300 text-sm sm:text-base mt-2">
                <strong>Phone:</strong> +91 7760556768
              </p>
              <p className="text-gray-400 dark:text-gray-300 text-sm sm:text-base mt-2">
                <strong>Email:</strong> support@jnanakshi.com
              </p>
            </div>
          </div>

          <div className="mt-8 sm:mt-10 border-t border-gray-700 dark:border-dark-600 pt-4 sm:pt-6 flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Social Media Links */}
            <div className="flex space-x-4 sm:space-x-6">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 dark:text-gray-300 hover:text-white transition text-lg sm:text-xl"
              >
                <i className="fab fa-facebook-f"></i>
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 dark:text-gray-300 hover:text-white transition text-lg sm:text-xl"
              >
                <i className="fab fa-twitter"></i>
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 dark:text-gray-300 hover:text-white transition text-lg sm:text-xl"
              >
                <i className="fab fa-linkedin-in"></i>
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 dark:text-gray-300 hover:text-white transition text-lg sm:text-xl"
              >
                <i className="fab fa-instagram"></i>
              </a>
            </div>

            {/* Copyright */}
            <p className="text-gray-400 dark:text-gray-300 text-xs sm:text-sm text-center sm:text-right">
              &copy; {new Date().getFullYear()} Jnanakshi Solutions. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* Chatbot Component */}
      <Chatbot />
    </div>
  );
};

export default Home;
